import { Autocomplete, styled, TextField, Typography } from '@chronosphereio/chrono-ui';
import { DurationString, getDurationStringMilliseconds, isDurationString } from '@chronosphereio/core';
import constate from 'constate';
import { JsonParam } from 'use-query-params';
import { isValidResolutionLevel, QUERY_OPTIONS_URL_PARAM, QueryOptionsDefinition, QueryResolution } from '@/core';
import { useOptionalQueryParamState } from '@/plugin-system/utils/url';

export const AUTO_RESOLUTION = '__auto';
export const AUTO_RESOLUTION_HIGH = '__auto_high';
export const AUTO_RESOLUTION_LOW = '__auto_low';

type ResolutionOption = {
  id: string;
  title: string;
  group: string;
};

const AUTO_RESOLUTION_OPTIONS: ResolutionOption[] = [
  { id: AUTO_RESOLUTION_LOW, title: 'Low', group: 'Automatic' },
  { id: AUTO_RESOLUTION, title: 'Medium', group: 'Automatic' },
  { id: AUTO_RESOLUTION_HIGH, title: 'High', group: 'Automatic' },
];

const FIXED_RESOLUTION_OPTIONS: ResolutionOption[] = ['30s', '1m', '5m', '10m', '30m', '1h', '12h', '1d'].map(
  (duration) => ({
    id: duration,
    title: duration,
    group: 'Fixed',
  })
);

const ALL_RESOLUTION_OPTIONS: ResolutionOption[] = [...AUTO_RESOLUTION_OPTIONS, ...FIXED_RESOLUTION_OPTIONS];

export type StepResolutionArgs = {
  enableQueryParams?: boolean;
  initialStepResolution?: string;
};

function useStepResolutionStoreInternal({
  enableQueryParams = false,
  initialStepResolution = AUTO_RESOLUTION,
}: StepResolutionArgs) {
  const [step, setStep] = useOptionalQueryParamState<QueryOptionsDefinition>({
    queryParamName: QUERY_OPTIONS_URL_PARAM,
    enableQueryParam: enableQueryParams,
    initialValue: { resolution: initialStepResolution },
    paramConfig: JsonParam,
  });
  return {
    step,
    setStep,
  };
}

const [StepStepResolutionProvider, useStepResolutionStore] = constate(useStepResolutionStoreInternal);

export { StepStepResolutionProvider as StepResolutionProvider };

export function useStepResolution() {
  try {
    return useStepResolutionStore().step.resolution;
  } catch (_e) {
    return '__auto';
  }
}

export function useStaticStepResolutionMs(queryResolution?: QueryResolution) {
  let resolution = useStepResolution();
  // Resolution set in query_options overrides resolution from context
  if (queryResolution !== undefined) {
    resolution = queryResolution;
  }

  if (resolution !== undefined && (resolution.includes('auto') || !isDurationString(resolution))) {
    return null;
  }

  // Otherwise try to parse the resolution
  return getDurationStringMilliseconds(resolution as DurationString);
}

interface StepResolutionControlsProps {
  /** Min-width for the underlying `Autocomplete`. Defaults to 110px. */
  minWidth?: number | string;
}

export function StepResolutionControls({ minWidth = 110 }: StepResolutionControlsProps = {}) {
  const { step, setStep } = useStepResolutionStore();
  const selectedOption =
    ALL_RESOLUTION_OPTIONS.find((option) => option.id === step.resolution) ?? AUTO_RESOLUTION_OPTIONS[1];
  return (
    <Autocomplete
      sx={{ minWidth }}
      disableClearable
      blurOnSelect
      options={ALL_RESOLUTION_OPTIONS}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.title}
      value={selectedOption}
      onChange={(_, value) => setStep({ resolution: value.id })}
      renderInput={(params) => <TextField {...params} label="Resolution" />}
      renderGroup={(params) => (
        <li key={params.group}>
          {params.group.length > 0 && (
            <GroupHeader>
              <Typography variant="body2" color={(theme) => theme.palette.text.legacySecondary}>
                <strong>{params.group}</strong>
              </Typography>
            </GroupHeader>
          )}
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Option>{option.title}</Option>
        </li>
      )}
    />
  );
}

export function getResolutionUrlParam(resolution: QueryResolution): string {
  if (isValidResolutionLevel(resolution)) {
    const resolutionToAutoUrlParam = {
      Low: AUTO_RESOLUTION_LOW,
      Medium: AUTO_RESOLUTION,
      High: AUTO_RESOLUTION_HIGH,
    } as const;
    return resolutionToAutoUrlParam[resolution];
  }
  // Allow custom durations to be set in query_options.resolution param
  return resolution;
}

const Option = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  whiteSpace: 'nowrap',
  width: '100%',
});

const GroupHeader = styled('div')(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.border.default}`,
}));

const GroupItems = styled('ul')({
  padding: 0,
});
